/**
 * Pin the shell to what the phone is actually showing.
 *
 * `position: fixed; inset: 0` sizes the shell to the *layout* viewport. On
 * iOS that is not always what is on screen: after the keyboard closes Safari
 * can leave the visual viewport scrolled inside the layout one, and in-app
 * browsers (Instagram, WhatsApp) report a layout viewport taller than the
 * visible area. Either way the CTA ends up mid-screen with blank paper under
 * it. `visualViewport` reports the visible box, so the shell follows that
 * whenever it is known — and is left alone while an input has focus, so the
 * step does not re-fit every time the keyboard slides in.
 */
export function followVisualViewport(): void {
  const vv = window.visualViewport;
  if (!vv) return;
  const root = document.documentElement;

  const typing = () => {
    const el = document.activeElement;
    return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || (el as HTMLElement).isContentEditable);
  };

  let raf = 0;
  const sync = () => {
    cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      if (typing() || vv.scale !== 1) return;
      // the fixed shell cannot scroll into view; any offset is a leftover
      if (window.scrollX || window.scrollY) window.scrollTo(0, 0);
      const height = Math.round(vv.height);
      if (height > 200 && height <= window.innerHeight) {
        root.style.setProperty('--fk-vv-top', `${Math.round(vv.offsetTop)}px`);
        root.style.setProperty('--fk-vv-height', `${height}px`);
      } else {
        root.style.removeProperty('--fk-vv-top');
        root.style.removeProperty('--fk-vv-height');
      }
    });
  };

  vv.addEventListener('resize', sync);
  vv.addEventListener('scroll', sync);
  window.addEventListener('orientationchange', sync);
  window.addEventListener('pageshow', sync);
  // the keyboard closing is the case that leaves the offset behind
  document.addEventListener('focusout', () => setTimeout(sync, 60));
  sync();
}
